import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { proposalsApi } from '../../services/api';
import type { Proposal, ProposalStatus } from '../../types';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import VersionHistoryModal from '../../components/Proposals/VersionHistoryModal';

const statusConfig: Record<ProposalStatus, { label: string; color: string }> = {
  'novo_lead': { label: 'Novo Lead', color: 'bg-gray-100 text-gray-800' },
  'qualificacao': { label: 'Qualificação', color: 'bg-blue-100 text-blue-800' },
  'em_elaboracao': { label: 'Em Elaboração', color: 'bg-yellow-100 text-yellow-800' },
  'aguardando_aprovacao': { label: 'Aguard. Aprovação', color: 'bg-orange-100 text-orange-800' },
  'enviada_cliente': { label: 'Enviada Cliente', color: 'bg-indigo-100 text-indigo-800' },
  'em_negociacao': { label: 'Em Negociação', color: 'bg-purple-100 text-purple-800' },
  'em_analise_cliente': { label: 'Análise Cliente', color: 'bg-cyan-100 text-cyan-800' },
  'aprovada': { label: 'Aprovada', color: 'bg-green-100 text-green-800' },
  'em_geracao_contrato': { label: 'Ger. Contrato', color: 'bg-emerald-100 text-emerald-800' },
  'aguardando_assinatura': { label: 'Aguard. Assinatura', color: 'bg-teal-100 text-teal-800' },
  'assinado': { label: 'Assinado', color: 'bg-green-100 text-green-800' },
  'em_execucao': { label: 'Em Execução', color: 'bg-green-100 text-green-800' },
  'cancelada': { label: 'Cancelada', color: 'bg-red-100 text-red-800' },
  'perdida': { label: 'Perdida', color: 'bg-red-100 text-red-800' },
  'rejeitada_assinatura': { label: 'Rejeitada', color: 'bg-red-100 text-red-800' },
};

const ProposalsList: React.FC = () => {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Valor do input (atualiza imediatamente)
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedProposalForHistory, setSelectedProposalForHistory] = useState<{ id: string; number: string } | null>(null);

  // Debounce para busca - só busca com 3+ caracteres ou se estiver vazio
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setSearchTerm(searchInput);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadProposals = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params: any = { page, page_size: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const response = await proposalsApi.getProposals(params);
      setProposals(response.results || []);
      setTotalPages(Math.ceil((response.count || 0) / pageSize));
    } catch (err: any) {
      console.error('Erro ao carregar propostas:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar propostas');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter, pageSize]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadProposals();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleGeneratePdf = async (proposalId: string, proposalNumber: string) => {
    try {
      const blob = await proposalsApi.generatePdf(proposalId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposta_${proposalNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Erro ao gerar PDF:', err);
      setError(err.response?.data?.error || 'Erro ao gerar PDF');
    }
  };

  const handleOpenHistory = (proposalId: string, proposalNumber: string) => {
    setSelectedProposalForHistory({ id: proposalId, number: proposalNumber });
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistory = () => {
    setIsHistoryModalOpen(false);
    setSelectedProposalForHistory(null);
  };

  if (loading && proposals.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Propostas</h1>
          <p className="text-xs text-secondary-600 mt-0.5">
            Gerencie todas as propostas e negociações
          </p>
        </div>
        <Link
          to="/proposals/new"
          className="btn-primary flex items-center whitespace-nowrap"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Nova Proposta
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Buscar por número, cliente, cidade ou número legado (mín. 3 caracteres)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="min-w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="">Todos os Status</option>
              {Object.entries(statusConfig).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn-primary whitespace-nowrap">
            Buscar
          </button>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-2 py-2 rounded-lg text-xs">
          {error}
        </div>
      )}

      {/* Results Card */}
      <div className="card">
        {/* Responsive Cards for Mobile */}
        <div className="block lg:hidden space-y-3">
        {proposals.map((proposal) => (
          <div key={proposal.id} className="bg-white border border-secondary-200 rounded-lg p-3 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-secondary-900">{proposal.number}</h3>
                  {proposal.is_historical && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded border border-amber-300" title={`Contrato Histórico: ${proposal.legacy_contract_number}`}>
                      📜 HISTÓRICO
                    </span>
                  )}
                </div>
                <p className="text-xs text-secondary-500">v{proposal.version}</p>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                statusConfig[proposal.status]?.color || 'bg-gray-100 text-gray-800'
              }`}>
                {statusConfig[proposal.status]?.label || proposal.status}
              </span>
            </div>
            
            <div className="space-y-2 mb-3">
              <div className="text-sm font-medium text-secondary-900">{proposal.client_name}</div>
              <div className="text-xs text-secondary-600">{proposal.delivery_city}, {proposal.delivery_state}</div>
              <div className="text-sm font-medium text-green-600">{formatCurrency(proposal.total)}</div>
              <div className="text-xs text-secondary-500">{proposal.items_count} itens • {proposal.rental_period_days} dias</div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Link to={`/proposals/${proposal.id}`} className="text-primary-600 hover:text-primary-900 p-1" title="Ver">
                <EyeIcon className="h-4 w-4" />
              </Link>
              {proposal.is_historical ? (
                <button disabled className="text-gray-400 p-1 cursor-not-allowed" title="Contratos históricos não podem ser editados">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                  </svg>
                </button>
              ) : (
                <Link to={`/proposals/negotiate?id=${proposal.id}`} className="text-orange-600 hover:text-orange-900 p-1" title="Negociar">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                  </svg>
                </Link>
              )}
              <button onClick={() => handleOpenHistory(proposal.id, proposal.number)} className="text-purple-600 hover:text-purple-900 p-1" title="Histórico de Versões">
                <ClockIcon className="h-4 w-4" />
              </button>
              <button onClick={() => handleGeneratePdf(proposal.id, proposal.number)} className="text-green-600 hover:text-green-900 p-1" title="PDF">
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        </div>

        {/* Table for Desktop */}
        <div className="hidden lg:block bg-white shadow-sm rounded-lg border border-secondary-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Proposta</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Resp.</th>
                  <th className="px-2 py-1 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-100">
                {proposals.map((proposal) => (
                  <tr key={proposal.id} className="hover:bg-secondary-25">
                      <td className="px-2 py-1">
                        <span className="text-xs font-medium text-secondary-900">{proposal.number} v{proposal.version}</span>
                        {proposal.is_historical && (
                          <span className="inline-flex items-center px-1 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded border border-amber-300 ml-1" title={`Contrato Histórico: ${proposal.legacy_contract_number}`}>
                            📜
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1" title={proposal.client_name || ''}>
                        <span className="text-xs font-medium text-secondary-900">
                          {(proposal.client_name || '').length > 25 ? (proposal.client_name || '').substring(0, 25) + '...' : (proposal.client_name || '')}
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${
                          statusConfig[proposal.status]?.color || 'bg-gray-100 text-gray-800'
                        }`}>
                          {statusConfig[proposal.status]?.label || proposal.status}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-xs text-secondary-500 truncate max-w-24">{proposal.responsible_name}</td>
                      <td className="px-2 py-1 text-right">
                        <div className="flex justify-end space-x-1">
                          <Link to={`/proposals/${proposal.id}`} className="text-primary-600 hover:text-primary-900 p-0.5" title="Ver">
                            <EyeIcon className="h-3.5 w-3.5" />
                          </Link>
                          {proposal.is_historical ? (
                            <button disabled className="text-gray-400 p-0.5 cursor-not-allowed" title="Contratos históricos não podem ser editados">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-3.5 w-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                              </svg>
                            </button>
                          ) : (
                            <Link to={`/proposals/negotiate?id=${proposal.id}`} className="text-orange-600 hover:text-orange-900 p-0.5" title="Negociar">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-3.5 w-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                              </svg>
                            </Link>
                          )}
                          <button onClick={() => handleOpenHistory(proposal.id, proposal.number)} className="text-purple-600 hover:text-purple-900 p-0.5" title="Histórico de Versões">
                            <ClockIcon className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleGeneratePdf(proposal.id, proposal.number)} className="text-green-600 hover:text-green-900 p-0.5" title="PDF">
                            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-secondary-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-3 py-1.5 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50"
                >
                  Próximo
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center space-x-4">
                  <p className="text-xs text-secondary-700">
                    Página <span className="font-medium">{page}</span> de{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-secondary-700">Por página:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        const newSize = parseInt(e.target.value);
                        setPageSize(newSize);
                        setPage(1);
                      }}
                      className="text-sm border border-secondary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50"
                    >
                      Próximo
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {!loading && proposals.length === 0 && (
          <div className="text-center py-8">
            <div className="mx-auto h-12 w-12 text-secondary-400">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="mt-2 text-xs text-secondary-600">
              {searchTerm || statusFilter
                ? 'Nenhuma proposta encontrada. Tente ajustar os filtros de busca'
                : 'Nenhuma proposta encontrada. Comece criando sua primeira proposta'
              }
            </p>
          </div>
        )}
      </div>

      {/* Version History Modal */}
      {selectedProposalForHistory && (
        <VersionHistoryModal
          proposalId={selectedProposalForHistory.id}
          proposalNumber={selectedProposalForHistory.number}
          isOpen={isHistoryModalOpen}
          onClose={handleCloseHistory}
        />
      )}
    </div>
  );
};

export default ProposalsList;